import type { RequestFn } from '../http';
import { API_BASE } from '../http';
import type { AuditLog, CreateUserData, Submission, UpdateUserData, User } from '../types';

/**
 * Eine Kundenportal-Mail im Schaltbrett (Admin → Kundenportal → Mails).
 * Register und Begründung: backend/src/constants/customerPortalMails.ts
 */
export interface PortalMail {
  id: string;
  label: string;
  subject: string;
  description: string;
  /** Wer den Versand auslöst — die Frage, die beim Abschalten zählt. */
  kind: 'CUSTOMER_REQUEST' | 'MANUAL' | 'AUTOMATIC';
  /** Nicht abschaltbar: ohne sie ist das Portal nicht benutzbar. */
  locked?: boolean;
}

export interface PortalMailSettings {
  mails: PortalMail[];
  enabled: Record<string, boolean>;
}

/** Ein geändertes Feld einer nachträglichen Kunde/Rückfahrt-Korrektur. */
export interface SubmissionRetroEditChange {
  field: string;
  from: unknown;
  to: unknown;
}

/** Ein Eintrag der Änderungshistorie (Admin → Einsatz-Archiv, rein intern). */
export interface SubmissionRetroEditHistoryEntry {
  id: string;
  createdAt: string;
  userName: string | null;
  reason: string;
  changes: SubmissionRetroEditChange[];
}

/**
 * Kalender-Abos im Kundenportal (Admin → Kundenportal → Kalender-Abos).
 * Hintergrund: backend/src/lib/customerCalendarSubscriptions.ts
 */
export interface PortalCalendarSubscriptionOverview {
  /** Alte Firmen-Links werden noch angenommen (Übergang) */
  acceptLegacy: boolean;
  personal: { active: number; fetchedLast30Days: number };
  /** Firmen mit altem Link — zuletzt abgerufen zuerst */
  legacy: Array<{ customerId: string; name: string; lastUsedAt: string | null }>;
}

/**
 * Eine Leistung aus dem Portfolio. `key` ist der stabile Anker: Er landet als
 * `offeringKey` an Bericht-Vorschlägen und an den Bewertungen je Kunde. Umbenennen
 * entkoppelt beides lautlos — deshalb ist er im Editor nur bei neuen Einträgen frei.
 */
export interface PortfolioOffering {
  key: string;
  name: string;
  category: string;
  pitch: string;
  trigger: string;
  effortHint: string;
  valueHint: string;
  customerVisible?: boolean;
}

export interface MagicLinkRow {
  id: string;
  createdAt: string;
  expiresAt: string | null;
  consumedAt: string | null;
  revokedAt: string | null;
  archivedAt: string | null;
  label: string | null;
  createdBySource: string | null;
  verifyUrl: string | null;
  contactId: string | null;
  consumedByContactId: string | null;
}

export interface EmailPreviewTemplate {
  id: string;
  label: string;
  category: string;
  subject: string;
  html: string;
}

export type LlmRoutingProfileSource = 'default' | 'db' | 'env' | 'mixed';

export interface LlmRoutingProfileRow {
  useCase: string;
  tier: string;
  provider: string;
  model: string;
  temperature: number;
  maxTokens: number;
  jsonMode: boolean;
  stream: boolean;
  escalateOnError: boolean;
  maxPromptChars?: number;
  multimodal: boolean;
  /** undefined = Standard des Modells */
  reasoning?: LlmReasoningLevel;
  source: LlmRoutingProfileSource;
  fieldSources: Record<string, string>;
}

export type LlmTier = 'fast' | 'balanced' | 'deep';

export type LlmReasoningLevel = 'off' | 'minimal' | 'low' | 'medium' | 'high' | 'xhigh' | 'max';

/** Modell aus dem Katalog des Anbieters (OpenRouter), s. backend/src/services/llm/modelCatalog.ts */
export interface LlmCatalogModel {
  id: string;
  name: string;
  vendor: string | null;
  description: string | null;
  contextLength: number | null;
  maxCompletionTokens: number | null;
  inputModalities: string[];
  outputModalities: string[];
  inputUsdPerMToken: number | null;
  outputUsdPerMToken: number | null;
  cacheReadUsdPerMToken: number | null;
  tieredAboveTokens: number | null;
  supportsJson: boolean | null;
  supportsTools: boolean | null;
  supportsVision: boolean | null;
  reasoning: 'none' | 'optional' | 'mandatory' | null;
  /** Wählbare Stufen; null = keine Liste vom Anbieter */
  reasoningEfforts: string[] | null;
  /** Denkt das Modell ohne Angabe von selbst? */
  reasoningDefaultOn: boolean | null;
  reasoningDefaultEffort: string | null;
  isFree: boolean;
  createdAt: string | null;
  knowledgeCutoff: string | null;
  expiresAt: string | null;
  /** Stärke laut Artificial Analysis; null = kein passender Eintrag / kein Schlüssel */
  benchmark?: LlmModelBenchmark | null;
}

export interface LlmModelBenchmark {
  /** Intelligence Index 0–100 */
  intelligence: number | null;
  coding: number | null;
  math: number | null;
  tokensPerSecond: number | null;
  ttftSeconds: number | null;
  /** Modellname bei Artificial Analysis (womit zugeordnet wurde) */
  sourceName: string;
}

export interface LlmModelCatalog {
  kind: 'openrouter' | 'basic' | 'none';
  baseUrl: string | null;
  fetchedAt: string;
  models: LlmCatalogModel[];
  error?: string;
  usdToEur: number;
  benchmarks?: {
    keyConfigured: boolean;
    keyFromEnv: boolean;
    fetchedAt: string | null;
    matched: number;
    error: string | null;
  };
}

export interface LlmConnectionHealth {
  /** Circuit Breaker offen: wird bis dahin übersprungen */
  skippedUntil: string | null;
  consecutiveFailures: number;
  lastError: string | null;
  lastErrorAt: string | null;
  lastSuccessAt: string | null;
  failoversTo: number;
}

export interface LlmFailoverConnection {
  id: string;
  label: string;
  enabled: boolean;
  provider: 'openai' | 'vertex';
  baseUrl: string | null;
  region: string | null;
  models: Partial<Record<LlmTier, string>>;
  apiKeySet: boolean;
  keyHint: string | null;
  serviceAccountSet: boolean;
  encrypted: boolean;
  priority: number;
  health: LlmConnectionHealth;
  failoverCalls30d: number;
}

export interface LlmFailoverConnectionInput {
  label: string;
  enabled: boolean;
  provider: 'openai' | 'vertex';
  baseUrl?: string;
  apiKey?: string;
  serviceAccountJson?: string;
  region?: string;
  models: Partial<Record<LlmTier, string>>;
}

export interface LlmConnectionsState {
  primary: {
    id: string;
    label: string;
    provider: 'openai' | 'vertex';
    baseUrl: string | null;
    health: LlmConnectionHealth;
  } | null;
  connections: LlmFailoverConnection[];
  maxConnections: number;
  encryptionAvailable: boolean;
}

export interface LlmUseCaseUsage {
  useCase: string;
  calls: number;
  tokensInput: number;
  tokensOutput: number;
  costEur: number | null;
}

/** Eine Zeile aus dem Lauf-Protokoll des Weclapp-Spiegels. */
export interface WeclappMirrorRun {
  id: string;
  /** tickets | comments | timeRecords */
  area: string;
  /** delta | backfill | full */
  mode: string;
  /** schedule | chain | manual */
  trigger: string;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  candidates: number;
  processed: number;
  ticketsTouched: number;
  errorCount: number;
  errors: string[];
  samples: Array<{ ticketId: string; ticketNumber: string | null; subject: string | null }>;
  moreRemaining: boolean;
  cursorAt: string | null;
}

export function createAdminApi(request: RequestFn) {
  return {
    async getUsers() {
      return request<{ users: User[] }>('/admin/users');
    },

    async createUser(data: CreateUserData) {
      return request<{ user: User }>('/admin/users', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async updateUser(id: string, data: Partial<UpdateUserData>) {
      return request<{ user: User }>(`/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    async deleteUser(id: string) {
      return request<void>(`/admin/users/${id}`, { method: 'DELETE' });
    },

    /** Ansicht als Nutzer starten (nur Admin, 30 Min., nur lesen). */
    async startViewAs(userId: string) {
      return request<{ viewAs: { userId: string; displayName: string; role: string; expiresAt: string } }>(
        `/admin/view-as/${encodeURIComponent(userId)}`,
        { method: 'POST' }
      );
    },

    /** Ansicht als Nutzer beenden — zurück zur eigenen Sicht. */
    async endViewAs() {
      return request<{ ended: boolean }>('/admin/view-as', { method: 'DELETE' });
    },

    async resendWelcomeEmail(id: string) {
      return request<{ ok: boolean }>(`/admin/users/${id}/resend-welcome`, { method: 'POST' });
    },

    /** `refresh` umgeht den Server-Cache — für gerade erst in weclapp angelegte Benutzer. */
    async getWeclappUsers(refresh = false) {
      return request<{ users: { id: string; name: string }[] }>(
        `/admin/weclapp/users${refresh ? '?refresh=1' : ''}`,
      );
    },

    /** Aktive Dienstleistungsartikel aus Weclapp (Auswahl für Zeitbuchungs-Aufgaben). */
    async getWeclappServiceArticles(refresh = false) {
      return request<{
        articles: { id: string; articleNumber: string; name: string; invoicingType: string | null; articleType: string | null }[];
      }>(`/admin/weclapp/service-articles${refresh ? '?refresh=1' : ''}`);
    },

    async getAdminSubmissions(status?: string) {
      const query = status ? `?status=${status}` : '';
      return request<{ submissions: Submission[] }>(`/admin/submissions${query}`);
    },


    /** Ticketfelder einer noch nicht übertragenen Submission korrigieren. */
    async updateSubmissionFields(
      id: string,
      body: Partial<{
        ticketSummary: string | null;
        ticketBoard: string | null;
        ticketType: string | null;
        ticketSubtype: string | null;
        ticketItem: string | null;
        ticketPriority: string | null;
        ticketInitialDescription: string | null;
        cwCompanyId: number | null;
        cwCompanyName: string | null;
        cwSiteId: number | null;
        cwContactId: number | null;
      }>
    ) {
      return request<{ submission: Submission }>(`/admin/submissions/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },

    /**
     * Kunde und/oder Rückfahrt an einem bereits übertragenen Bericht korrigieren
     * (nur CW_SUCCESS) — ohne PDF oder Unterschrift anzufassen, mit Grund im Audit-Log.
     */
    async retroEditSubmission(
      id: string,
      body: {
        reason: string;
        kunde?: { id: string; name: string };
        rueckfahrt?: { travelKmBack: number; travelMinutesBack: number };
      }
    ) {
      return request<{ submission: Submission; changes: SubmissionRetroEditChange[] }>(
        `/admin/submissions/${id}/retro-edit`,
        { method: 'PATCH', body: JSON.stringify(body) }
      );
    },

    /** Änderungshistorie der nachträglichen Kunde/Rückfahrt-Korrekturen eines Berichts. */
    async getSubmissionRetroEditHistory(id: string) {
      return request<{ entries: SubmissionRetroEditHistoryEntry[] }>(
        `/admin/submissions/${id}/retro-edit-history`
      );
    },

    /**
     * An den Techniker zurückgeben, mit Hinweis was zu tun ist — Push und Glocke
     * verlinken direkt in den Assistenten dieses Berichts.
     *
     * Ohne `note` bei einem bereits zurückgegebenen Bericht: Erinnerung mit dem
     * vorhandenen Hinweis, ohne ihn zu überschreiben.
     */
    async returnSubmissionToUser(id: string, note?: string) {
      return request<{ submission: Submission; reminder: boolean }>(
        `/admin/submissions/${id}/return`,
        {
          method: 'POST',
          body: JSON.stringify(note ? { note } : {}),
        }
      );
    },

    /** Mit Begründung stornieren (Begründung ist Pflicht). */
    async cancelSubmission(id: string, reason: string) {
      return request<{ submission: Submission }>(`/admin/submissions/${id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
    },

    /** Fehlgeschlagenen Bericht als erledigt ablegen, nicht übertragen (nur Admin, Begründung Pflicht). */
    async resolveSubmissionManually(id: string, reason: string) {
      return request<{ success: boolean }>(`/admin/submissions/${id}/resolve-manually`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
    },

    async retrySubmission(id: string) {
      return request<{ success: boolean; message: string }>(
        `/admin/submissions/${id}/retry`,
        { method: 'POST' }
      );
    },

    async getAdminInventorySessions(opts?: { page?: number; pageSize?: number }) {
      const params = new URLSearchParams();
      if (opts?.page) params.set('page', String(opts.page));
      if (opts?.pageSize) params.set('pageSize', String(opts.pageSize));
      const query = params.toString() ? `?${params.toString()}` : '';
      return request<{
        sessions: Array<{
          id: string;
          status: string;
          warehouseName: string;
          binName: string | null;
          cwAdjustmentId: number | null;
          lineCount: number;
          createdBy: { id: string; displayName: string } | null;
          startedAt: string;
          finalizedAt: string | null;
        }>;
      }>(`/admin/inventory-sessions${query}`);
    },

    async getConfig() {
      return request<{
        config: Record<string, unknown>;
        /** Aus aktueller Admin-Anfrage (Proxy/Host), ohne gespeicherte Portal-URL */
        detectedPublicBaseUrl?: string;
      }>('/admin/config');
    },

    /** Das Leistungsportfolio, wie es gerade gilt, plus Nutzung je Leistung. */
    async getAdminServicePortfolio() {
      return request<{
        portfolio: PortfolioOffering[];
        /** false = es gilt die Fassung aus dem Code, niemand hat je etwas geändert. */
        isCustom: boolean;
        house: PortfolioOffering[];
        usage: Record<string, { active: number; proposed: number; declined: number }>;
        categoryLabels: Record<string, string>;
        categoryOrder: string[];
      }>('/admin/service-portfolio');
    },

    /** Ersetzt die ganze Liste. `null` setzt auf die Fassung aus dem Code zurück. */
    async updateAdminServicePortfolio(portfolio: PortfolioOffering[] | null) {
      return request<{ success: true; portfolio: PortfolioOffering[]; isCustom: boolean }>(
        '/admin/service-portfolio',
        { method: 'PUT', body: JSON.stringify({ portfolio }) }
      );
    },

    async updateConfig(key: string, value: Record<string, unknown>) {
      return request<{ success: boolean }>('/admin/config', {
        method: 'PUT',
        body: JSON.stringify({ key, value }),
      });
    },

    async testConnectWise() {
      return request<{
        success: boolean;
        message: string;
        details?: {
          companyId?: string;
          ticketsFound?: number;
          sampleTicket?: { id: number; summary: string };
        };
      }>('/admin/config/test-connectwise');
    },

    async testConnectWiseFull() {
      return request<{
        success: boolean;
        message: string;
        timestamp: string;
        summary?: {
          boards: number;
          priorities: number;
          serviceLocations: number;
          sources: number;
          workTypes: number;
          workRoles: number;
          members: number;
          errors: number;
        };
        data?: Record<string, unknown>;
        errors?: string[];
      }>('/admin/config/test-connectwise-full');
    },

    async getAdminLlmRouting() {
      return request<{
        configured: boolean;
        provider: 'openai' | 'vertex' | null;
        profiles: LlmRoutingProfileRow[];
        /** Globale Auswahl „Modelle je Stufe" (null = Code-Standard je Use Case) */
        tierModels?: Record<LlmTier, string | null>;
        /** OpenRouter-Ersatzmodelle je Stufe (`models`-Parameter) */
        tierFallbacks?: Record<LlmTier, string[]>;
        tierReasoning?: Partial<Record<LlmTier, LlmReasoningLevel>>;
        primaryIsOpenRouter?: boolean;
        maxTierFallbacks?: number;
        defaults?: Record<string, { defaultTier: LlmTier; models: Record<LlmTier, string> }>;
      }>('/admin/llm/routing');
    },

    async getAdminLlmModels(refresh = false) {
      return request<LlmModelCatalog>(`/admin/llm/models${refresh ? '?refresh=1' : ''}`);
    },

    async getAdminKianUsageByUser(days = 60) {
      return request<KianUsageReport>(`/admin/kian/usage-by-user?days=${days}`);
    },

    async getAdminLlmUsage() {
      return request<{ days: number; since: string; useCases: LlmUseCaseUsage[] }>('/admin/llm/usage');
    },

    async saveAdminLlmTierModels(patch: Partial<Record<LlmTier, string | null>>) {
      return request<{ success: boolean }>('/admin/llm/routing/tiers', {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
    },

    async saveAdminLlmBenchmarkKey(apiKey: string | null) {
      return request<{ success: boolean }>('/admin/llm/benchmarks/key', {
        method: 'PUT',
        body: JSON.stringify({ apiKey }),
      });
    },

    async saveAdminLlmTierReasoning(patch: Partial<Record<LlmTier, LlmReasoningLevel | null>>) {
      return request<{ success: boolean }>('/admin/llm/routing/reasoning', {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
    },

    async saveAdminLlmUseCase(
      useCase: string,
      patch: { defaultTier?: LlmTier | null; model?: string | null; reasoning?: LlmReasoningLevel | null }
    ) {
      return request<{ success: boolean }>(`/admin/llm/routing/use-case/${encodeURIComponent(useCase)}`, {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
    },

    async saveAdminLlmTierFallbacks(patch: Partial<Record<LlmTier, string[] | null>>) {
      return request<{ success: boolean }>('/admin/llm/routing/fallbacks', {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
    },

    async getAdminLlmConnections() {
      return request<LlmConnectionsState>('/admin/llm/connections');
    },

    async createAdminLlmConnection(input: LlmFailoverConnectionInput) {
      return request<{ success: boolean }>('/admin/llm/connections', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },

    async updateAdminLlmConnection(id: string, input: LlmFailoverConnectionInput) {
      return request<{ success: boolean }>(`/admin/llm/connections/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(input),
      });
    },

    async deleteAdminLlmConnection(id: string) {
      return request<{ success: boolean }>(`/admin/llm/connections/${encodeURIComponent(id)}`, { method: 'DELETE' });
    },

    async reorderAdminLlmConnections(ids: string[]) {
      return request<{ success: boolean }>('/admin/llm/connections/order', {
        method: 'PUT',
        body: JSON.stringify({ ids }),
      });
    },

    async resetAdminLlmConnectionHealth(id: string) {
      return request<{ success: boolean }>(`/admin/llm/connections/${encodeURIComponent(id)}/reset-health`, {
        method: 'POST',
      });
    },

    async getAdminLlmConnectionModels(id: string) {
      return request<{ models: LlmCatalogModel[] }>(`/admin/llm/connections/${encodeURIComponent(id)}/models`, {
        method: 'POST',
      });
    },

    async testAdminLlmConnection(id: string) {
      return request<{
        success: boolean;
        error?: string;
        results: Array<{ model: string; tiers: LlmTier[]; ok: boolean; latencyMs: number; error?: string }>;
      }>(`/admin/llm/connections/${encodeURIComponent(id)}/test`, { method: 'POST' });
    },

    async resetAdminLlmUseCase(useCase: string) {
      return request<{ success: boolean }>(`/admin/llm/routing/use-case/${encodeURIComponent(useCase)}`, {
        method: 'DELETE',
      });
    },

    async getAuditLogs(limit = 100, offset = 0, action?: string) {
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });
      if (action) params.set('action', action);
      return request<{ logs: AuditLog[] }>(`/admin/audit-logs?${params.toString()}`);
    },

    async getAdminSyncStatus() {
      return request<{
        state: 'IDLE' | 'RUNNING' | 'PAUSED' | 'COMPLETED' | 'FAILED';
        job: {
          id: string;
          state: string;
          startedAt: string;
          completedAt?: string;
          startedByUserId: string;
          options: {
            customerId?: string;
            statusIds?: string[];
            limit?: number;
          };
          progress: {
            total: number;
            processed: number;
            succeeded: number;
            failed: number;
          };
          consecutiveFailures: number;
          pauseReason?: string;
        } | null;
      }>('/admin/sync/status');
    },

    async startAdminSync(options: {
      customerId?: string;
      statusIds?: string[];
      mappingProfileId?: string;
      limit?: number;
    }) {
      return request<{ success: boolean; jobId: string }>('/admin/sync/start', {
        method: 'POST',
        body: JSON.stringify(options),
      });
    },

    async pauseAdminSync() {
      return request<{ success: boolean; message: string }>('/admin/sync/pause', {
        method: 'POST',
      });
    },

    async resumeAdminSync() {
      return request<{ success: boolean; message: string }>('/admin/sync/resume', {
        method: 'POST',
      });
    },

    async getAdminSyncLog(
      params: { state?: string; search?: string; limit?: number; offset?: number } = {}
    ) {
      const query = new URLSearchParams();
      if (params.state) query.set('state', params.state);
      if (params.search) query.set('search', params.search);
      if (params.limit) query.set('limit', String(params.limit));
      if (params.offset) query.set('offset', String(params.offset));
      const qs = query.toString();
      return request<{
        entries: Array<{
          id: string;
          weclappTicketId: string;
          weclappTicketNumber?: string;
          weclappSubject?: string;
          cwTicketId?: number;
          cwCompanyId?: number;
          syncState: string;
          lastSyncAt?: string;
          lastError?: string;
          syncAttempts: number;
          createdAt: string;
          updatedAt: string;
        }>;
        total: number;
        limit: number;
        offset: number;
      }>(`/admin/sync/log${qs ? `?${qs}` : ''}`);
    },

    async retryAdminSyncEntry(id: string) {
      return request<{ success: boolean; cwTicketId?: number; error?: string }>(
        `/admin/sync/retry/${id}`,
        { method: 'POST' }
      );
    },

    async deleteAdminSyncEntry(id: string) {
      return request<{ success: boolean; message: string }>(
        `/admin/sync/entry/${id}`,
        { method: 'DELETE' }
      );
    },

    async getAdminSyncStatistics() {
      return request<{
        tickets: {
          total: number;
          synced: number;
          queued: number;
          errors: number;
          new: number;
        };
        attachments: { total: number; synced: number; errors: number };
      }>('/admin/sync/statistics');
    },

    // Microsoft SSO Auto-Setup (per Graph CLI)
    async startMicrosoftAutoSetup() {
      return request<{
        sessionId: string;
        verificationUriComplete: string;
        verificationUri: string;
        userCode: string;
        expiresIn: number;
        pollIntervalMs: number;
      }>('/admin/microsoft-sso/auto-setup', { method: 'POST', body: '{}' });
    },

    async pollMicrosoftAutoSetup(sessionId: string) {
      const q = new URLSearchParams({ sessionId });
      return request<
        | { status: 'pending'; pollIntervalMs: number }
        | { status: 'error'; message: string }
        | {
            status: 'ok';
            tenantId: string;
            clientId: string;
            displayName: string;
            clientSecretOnce?: string;
            portalUrl?: string | null;
          }
      >(`/admin/microsoft-sso/auto-setup/poll?${q.toString()}`);
    },

    // Office-Vorschau (SharePoint „servicehub-previews"): Status + Auto-Setup
    async getOfficePreviewStatus() {
      return request<{ configured: boolean; siteWebUrl: string | null; configuredAt: string | null }>(
        '/admin/office-preview/status'
      );
    },

    async startOfficePreviewAutoSetup() {
      return request<{
        sessionId: string;
        verificationUriComplete: string;
        verificationUri: string;
        userCode: string;
        expiresIn: number;
        pollIntervalMs: number;
      }>('/admin/office-preview/auto-setup', { method: 'POST', body: '{}' });
    },

    async pollOfficePreviewAutoSetup(sessionId: string) {
      const q = new URLSearchParams({ sessionId });
      return request<
        | { status: 'pending'; pollIntervalMs: number }
        | { status: 'error'; message: string }
        | { status: 'ok'; siteWebUrl: string; steps: string[] }
      >(`/admin/office-preview/auto-setup/poll?${q.toString()}`);
    },

    // Kundenportal-SSO: Setup-Status (Admin-Hinweis-Banner) + Auto-Setup multi-tenant App
    /**
     * Einrichtungsstand der Weclapp-Status-IDs für den Servicebericht. Fehlen sie,
     * bleiben mitbearbeitete Tickets nach dem Absenden offen liegen.
     */
    async getServiceReportStatusSetup() {
      return request<{
        applies: boolean;
        resolvedConfigured: boolean;
        inProgressConfigured: boolean;
        /** Zusatztickets, die deswegen bereits ohne Statuswechsel liegen geblieben sind */
        stuckTickets: number;
      }>('/service-report/status-setup');
    },

    async getCustomerSsoSetupStatus() {
      return request<{
        microsoftConfigured: boolean;
        googleConfigured: boolean;
        portalUrlSet: boolean;
      }>('/admin/customer-sso/setup-status');
    },

    async startCustomerSsoAutoSetup() {
      return request<{
        sessionId: string;
        verificationUriComplete: string;
        verificationUri: string;
        userCode: string;
        expiresIn: number;
        pollIntervalMs: number;
      }>('/admin/customer-sso/auto-setup', { method: 'POST', body: '{}' });
    },

    async pollCustomerSsoAutoSetup(sessionId: string) {
      const q = new URLSearchParams({ sessionId });
      return request<
        | { status: 'pending'; pollIntervalMs: number }
        | { status: 'error'; message: string }
        | { status: 'ok'; tenantId: string; clientId: string; displayName: string }
      >(`/admin/customer-sso/auto-setup/poll?${q.toString()}`);
    },

    // Microsoft SSO: Web-Redirect-URI bei bestehender App ergänzen (fixt AADSTS50011)
    async startMicrosoftUpdateRedirectUri() {
      return request<{
        sessionId: string;
        verificationUriComplete: string;
        verificationUri: string;
        userCode: string;
        expiresIn: number;
        pollIntervalMs: number;
        targetRedirectUri: string;
      }>('/admin/microsoft-sso/update-redirect-uri', { method: 'POST', body: '{}' });
    },

    async pollMicrosoftUpdateRedirectUri(sessionId: string) {
      const q = new URLSearchParams({ sessionId });
      return request<
        | { status: 'pending'; pollIntervalMs: number }
        | { status: 'error'; message: string }
        | {
            status: 'ok';
            displayName: string;
            redirectUri: string;
            added: boolean;
            redirectUris: string[];
          }
      >(`/admin/microsoft-sso/update-redirect-uri/poll?${q.toString()}`);
    },

    async testMicrosoftGraphMail(body?: { to?: string }) {
      return request<{ success: boolean; message: string }>(
        '/admin/microsoft-sso/test-graph-mail',
        {
          method: 'POST',
          body: JSON.stringify(body ?? {}),
        }
      );
    },

    async startGraphMailDeviceConnect() {
      return request<{
        sessionId: string;
        verificationUriComplete: string;
        verificationUri: string;
        userCode: string;
        expiresIn: number;
        pollIntervalMs: number;
      }>('/admin/microsoft-sso/graph-mail-device/start', { method: 'POST', body: '{}' });
    },

    async pollGraphMailDevice(sessionId: string) {
      const q = new URLSearchParams({ sessionId });
      return request<
        | { status: 'pending'; pollIntervalMs: number }
        | { status: 'error'; message: string }
        | { status: 'ok'; delegatedUserEmail: string }
      >(`/admin/microsoft-sso/graph-mail-device/poll?${q.toString()}`);
    },

    async getHubWeclappOptions() {
      return request<{
        statuses: { id: string; name: string }[];
        priorities: { id: string; name: string }[];
      }>('/admin/hub-config/weclapp-options');
    },

    /** Phase 2.10: CSM-Vorabkontakt bestätigen – Freigabe für Magic-Link-Mail bei aktivem Gate. */
    async markCustomerCsmPreContact(contactId: string, body?: { completedAt?: string }) {
      return request<{
        contact: { id: string; csmPreContactCompletedAt: string | null };
      }>(`/admin/customer-contacts/${contactId}/csm-pre-contact`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      });
    },

    /** Kunden-Portal Phase 4: Trust Center (SLA-Kachel im Portal) */
    async getCustomerPortalTrust() {
      return request<{
        value: {
          quarterLabel: string;
          slaPercent: number;
          trend12m: number[];
          footnote?: string;
        } | null;
      }>('/admin/customer-portal/trust');
    },

    async putCustomerPortalTrust(body: {
      quarterLabel: string;
      slaPercent: number;
      trend12m: number[];
      footnote?: string;
    }) {
      return request<{ ok: boolean }>('/admin/customer-portal/trust', {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },

    async getCustomerPortalTrustComputed() {
      return request<{
        quarterLabel: string;
        slaPercent: number | null;
        trend12m: number[];
      }>('/admin/customer-portal/trust/computed');
    },

    /** Kunden-Portal Phase 4: öffentlicher Störungs-/Status-Banner */
    async getCustomerPortalPublicStatusConfig() {
      return request<{
        value: { degraded: boolean; message: string; updatedAt: string } | null;
      }>('/admin/customer-portal/status');
    },

    async putCustomerPortalPublicStatusConfig(body: { degraded: boolean; message: string }) {
      return request<{ ok: boolean }>('/admin/customer-portal/status', {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },

    // ─── Globale Meldungen (Notices) ──────────────────────────────────────
    async listGlobalNotices() {
      return request<{
        items: Array<{
          id: string;
          type: 'STOERUNG' | 'WARTUNG' | 'INFO';
          title: string;
          message: string;
          isActive: boolean;
          targetMode: 'ALL' | 'TAGS';
          targetTags: string[];
          validUntil: string | null;
          createdAt: string;
          updatedAt: string;
        }>;
      }>('/admin/customer-portal/notices');
    },

    async createGlobalNotice(body: {
      type: 'STOERUNG' | 'WARTUNG' | 'INFO';
      title: string;
      message: string;
      isActive: boolean;
      targetMode: 'ALL' | 'TAGS';
      targetTags: string[];
      validUntil?: string | null;
    }) {
      return request<{ id: string }>('/admin/customer-portal/notices', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async updateGlobalNotice(
      id: string,
      body: {
        type: 'STOERUNG' | 'WARTUNG' | 'INFO';
        title: string;
        message: string;
        isActive: boolean;
        targetMode: 'ALL' | 'TAGS';
        targetTags: string[];
        validUntil?: string | null;
      }
    ) {
      return request<{ id: string }>(`/admin/customer-portal/notices/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },

    async deleteGlobalNotice(id: string) {
      return request<{ ok: boolean }>(`/admin/customer-portal/notices/${id}`, {
        method: 'DELETE',
      });
    },

    async getNoticeTags() {
      return request<{ tags: string[] }>('/admin/customer-portal/notice-tags');
    },

    async patchCustomerTags(customerId: string, tags: string[]) {
      return request<{ id: string; tags: string[] }>(`/admin/customers/${customerId}/tags`, {
        method: 'PATCH',
        body: JSON.stringify({ tags }),
      });
    },

    /** Phase 4: CSAT-Ticketfeedback (intern) */
    async getCustomerPortalTicketCsat() {
      return request<{
        items: Array<{
          id: string;
          score: number | null;
          comment: string | null;
          ticketReference: string | null;
          createdAt: string;
          contact: {
            name: string;
            email: string;
            cwCompanyId: number | null;
            weclappCustomerId: string | null;
          } | null;
        }>;
      }>('/admin/customer-portal/feedback');
    },

    async getCustomerPortalPhase5Proactive() {
      return request<{
        value: { warrantyNoticeDaysBefore: number[]; maintenanceIntervalDays: number };
      }>('/admin/customer-portal/phase5-proactive');
    },

    async putCustomerPortalPhase5Proactive(body: {
      warrantyNoticeDaysBefore: number[];
      maintenanceIntervalDays: number;
    }) {
      return request<{ ok: boolean }>('/admin/customer-portal/phase5-proactive', {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },

    async getCustomerPortalNps() {
      return request<{
        trend: Array<{
          key: string;
          nps: number;
          promoters: number;
          passives: number;
          detractors: number;
          count: number;
        }>;
        items: Array<{
          id: string;
          score: number | null;
          comment: string | null;
          ticketReference: string | null;
          createdAt: string;
          contact: {
            name: string;
            email: string;
            cwCompanyId: number | null;
            weclappCustomerId: string | null;
          } | null;
        }>;
      }>('/admin/customer-portal/nps');
    },

    async getCustomerPortalPortalFeedback() {
      return request<{
        items: Array<{
          id: string;
          score: number | null;
          comment: string | null;
          createdAt: string;
          contact: {
            name: string;
            email: string;
            cwCompanyId: number | null;
            weclappCustomerId: string | null;
          } | null;
        }>;
      }>('/admin/customer-portal/feedback/portal');
    },

    // ─── Admin: Kundenportal-Verwaltung ────────────────────────────────────
    async createCustomerContact(data: {
      email: string;
      name: string;
      cwCompanyId?: number;
      weclappCustomerId?: string;
    }) {
      return request<{
        contact: {
          id: string;
          email: string;
          name: string;
          cwCompanyId: number | null;
          weclappCustomerId: string | null;
          role: string;
          isActive: boolean;
          createdAt: string;
          updatedAt: string;
        };
      }>('/admin/customer-contacts', { method: 'POST', body: JSON.stringify(data) });
    },

    async listCustomerContacts(params: {
      q?: string;
      role?: 'ADMIN' | 'MITARBEITER' | 'GAST';
      isActive?: boolean;
      cwCompanyId?: number;
      cursor?: string;
      limit?: number;
    } = {}) {
      const qs = new URLSearchParams();
      if (params.q) qs.set('q', params.q);
      if (params.role) qs.set('role', params.role);
      if (params.isActive !== undefined) qs.set('isActive', String(params.isActive));
      if (params.cwCompanyId !== undefined) qs.set('cwCompanyId', String(params.cwCompanyId));
      if (params.cursor) qs.set('cursor', params.cursor);
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      const query = qs.toString();
      return request<{
        items: Array<{
          id: string;
          email: string;
          name: string;
          phone: string | null;
          role: string;
          isActive: boolean;
          portalEnabled: boolean;
          isPortalDummy: boolean;
          emailHardBouncedAt: string | null;
          csmPreContactCompletedAt: string | null;
          cwCompanyId: number | null;
          weclappCustomerId: string | null;
          createdAt: string;
          updatedAt: string;
          _count: { sessions: number; magicLinkTokens: number; auditLogs: number };
        }>;
        nextCursor: string | null;
      }>(`/admin/customer-contacts${query ? `?${query}` : ''}`);
    },

    async getCustomerContact(contactId: string) {
      return request<{
        contact: {
          id: string;
          email: string;
          name: string;
          phone: string | null;
          role: string;
          isActive: boolean;
          portalEnabled: boolean;
          emailHardBouncedAt: string | null;
          csmPreContactCompletedAt: string | null;
          cwCompanyId: number | null;
          weclappCustomerId: string | null;
          capabilities: Record<string, boolean>;
          ticketViewMode: 'OWN' | 'INCLUDE' | 'EXCLUDE' | 'ALL';
          ticketViewContactIds: string[];
          createdAt: string;
          updatedAt: string;
        };
        sessions: Array<{
          id: string;
          deviceLabel: string | null;
          isActive: boolean;
          createdAt: string;
          lastSeenAt: string;
          expiresAt: string;
          revokedAt: string | null;
          revokedByAdminId: string | null;
        }>;
        magicLinks: Array<{
          id: string;
          createdAt: string;
          expiresAt: string;
          consumedAt: string | null;
          revokedAt: string | null;
          createdBySource: string | null;
          createdByAdminId: string | null;
          label: string | null;
          ipAddress: string | null;
        }>;
        /** „Zugriff im Portal": Schalterstellung je Bereich, wie das Portal sie auswertet */
        portalAccess: {
          /** Bereiche, die die Firma freigibt — nur die sind je Person schaltbar */
          companyModules: string[];
          areas: Record<string, boolean>;
          quoteAccept: boolean;
        };
      }>(`/admin/customer-contacts/${contactId}`);
    },

    async generateCustomerMagicLink(
      contactId: string,
      body: { sendEmail: boolean; label?: string; ttlMinutes: number }
    ) {
      return request<
        | { tokenId: string; rawToken: string; verifyUrl: string; expiresAt: string | null }
        | { tokenId: string; sent: boolean; expiresAt: string | null }
      >(`/admin/customer-contacts/${contactId}/magic-link`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async previewCustomerMagicLinkEmail(
      contactId: string,
      body: { ttlMinutes: number }
    ) {
      return request<{ subject: string; html: string; to: string; expiresMinutes: number }>(
        `/admin/customer-contacts/${contactId}/magic-link/preview`,
        { method: 'POST', body: JSON.stringify(body) }
      );
    },

    async setContactPortalAccess(contactId: string, enabled: boolean) {
      return request<{ ok: true; portalEnabled: boolean; revokedSessions: number }>(
        `/admin/customer-contacts/${contactId}/portal-access`,
        { method: 'PATCH', body: JSON.stringify({ enabled }) }
      );
    },

    async updateContactPermissions(
      contactId: string,
      data: {
        role?: 'ADMIN' | 'MITARBEITER' | 'GAST';
        ticketViewMode?: 'OWN' | 'INCLUDE' | 'EXCLUDE' | 'ALL';
        ticketViewContactIds?: string[];
        permissionOverrides?: Record<string, boolean>;
        /** Schalter je Bereich — der Server setzt die Rechte mit, die ein Bereich braucht */
        areas?: Record<string, boolean>;
        quoteAccept?: boolean;
      }
    ) {
      return request<{
        contact: {
          id: string;
          role: string;
          ticketViewMode: string;
          ticketViewContactIds: string[];
          capabilities: Record<string, boolean>;
        };
      }>(`/admin/customer-contacts/${contactId}/permissions`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    /** Register aller Portal-Bereiche + Standard-Freigabe */
    async getPortalModules() {
      return request<{
        modules: Array<{ id: string; label: string; description: string }>;
        defaults: string[];
      }>('/admin/portal-modules');
    },

    /** Freigabe eines Kunden: Hauptschalter + Bereiche */
    async getCustomerPortalModules(customerId: string) {
      return request<{
        customerId: string;
        name: string;
        portalAccessEnabled: boolean;
        /** Darf das Portal diesen Kunden nach Zufriedenheit fragen? Standard: nein. */
        feedbackEnabled: boolean;
        modules: Record<string, boolean>;
        /** false = nie gepflegt → es gilt die Standard-Freigabe */
        configured: boolean;
      }>(`/admin/customers/${customerId}/portal-modules`);
    },

    async updateCustomerPortalModules(
      customerId: string,
      data: {
        portalAccessEnabled?: boolean;
        feedbackEnabled?: boolean;
        modules?: Record<string, boolean>;
      }
    ) {
      return request<{
        customerId: string;
        portalAccessEnabled: boolean;
        feedbackEnabled: boolean;
        modules: Record<string, boolean>;
      }>(`/admin/customers/${customerId}/portal-modules`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    // ─── Kundenportal-Mails ────────────────────────────────────────────────────

    /**
     * Register aller Kundenportal-Mails + aktuelle Schalterstellung.
     * Register: backend/src/constants/customerPortalMails.ts
     */
    async getPortalMails() {
      return request<PortalMailSettings>('/admin/portal-mails');
    },

    /** Teil-Update: Nur die mitgeschickten Schalter ändern sich. */
    async updatePortalMails(enabled: Record<string, boolean>) {
      return request<PortalMailSettings>('/admin/portal-mails', {
        method: 'PATCH',
        body: JSON.stringify({ enabled }),
      });
    },

    // ─── Kundenportal-Kalender-Abos ────────────────────────────────────────────

    /** Persönliche Abos und alte Firmen-Links, die noch abgerufen werden. */
    async getPortalCalendarSubscriptions() {
      return request<PortalCalendarSubscriptionOverview>('/admin/portal-calendar-subscriptions');
    },

    /** Alte Firmen-Links annehmen (Übergang) oder abschalten. */
    async updatePortalCalendarSubscriptions(acceptLegacy: boolean) {
      return request<PortalCalendarSubscriptionOverview>('/admin/portal-calendar-subscriptions', {
        method: 'PATCH',
        body: JSON.stringify({ acceptLegacy }),
      });
    },

    // ─── Konzern-Verwaltung ────────────────────────────────────────────────────

    async listCustomerGroups() {
      return request<{
        groups: Array<{
          id: string;
          name: string;
          note: string | null;
          customers: Array<{
            id: string;
            name: string;
            fixbyteCustomerNumber: string | null;
            portalAccessEnabled: boolean;
          }>;
        }>;
      }>('/admin/customer-groups');
    },

    async createCustomerGroup(data: { name: string; note?: string }) {
      return request<{ group: { id: string; name: string } }>('/admin/customer-groups', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /** Nur Name und Notiz — Firmen werden im Kunden-Hub angebunden (mit Bestätigung) */
    async updateCustomerGroup(groupId: string, data: { name?: string; note?: string | null }) {
      return request<{ ok: boolean }>(`/admin/customer-groups/${groupId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    /** Welche Schwestergesellschaften stehen für diesen Kontakt zur Wahl? */
    async getContactCompanyAccess(contactId: string) {
      return request<{
        contactId: string;
        homeCustomer: { id: string; name: string };
        groupId: string | null;
        siblings: Array<{
          id: string;
          name: string;
          portalAccessEnabled: boolean;
          availableModules: string[];
          granted: boolean;
          modules: Record<string, boolean>;
        }>;
      }>(`/admin/customer-contacts/${contactId}/company-access`);
    },

    async updateContactCompanyAccess(
      contactId: string,
      grants: Array<{ customerId: string; modules?: Record<string, boolean> }>
    ) {
      return request<{ ok: boolean }>(`/admin/customer-contacts/${contactId}/company-access`, {
        method: 'PUT',
        body: JSON.stringify({ grants }),
      });
    },

    async revokeCustomerMagicLink(tokenId: string) {
      return request<{ ok: boolean }>(`/admin/customer-magic-links/${tokenId}/revoke`, {
        method: 'POST',
        body: '{}',
      });
    },

    async archiveCustomerMagicLink(tokenId: string) {
      return request<{ ok: boolean }>(`/admin/customer-magic-links/${tokenId}/archive`, {
        method: 'POST',
        body: '{}',
      });
    },

    async unarchiveCustomerMagicLink(tokenId: string) {
      return request<{ ok: boolean }>(`/admin/customer-magic-links/${tokenId}/unarchive`, {
        method: 'POST',
        body: '{}',
      });
    },

    async listCustomerSessions(contactId: string) {
      return request<{
        items: Array<{
          id: string;
          deviceLabel: string | null;
          isActive: boolean;
          createdAt: string;
          lastSeenAt: string;
          expiresAt: string;
          revokedAt: string | null;
          revokedByAdminId: string | null;
        }>;
      }>(`/admin/customer-contacts/${contactId}/sessions`);
    },

    async revokeCustomerSession(sessionId: string) {
      return request<{ ok: boolean }>(`/admin/customer-sessions/${sessionId}/revoke`, {
        method: 'POST',
        body: '{}',
      });
    },

    async offboardCustomerContact(
      contactId: string,
      body: { newPrimaryContactId?: string; reason?: string; sendNotification?: boolean }
    ) {
      return request<{ ok: boolean; notificationSent: boolean; newPrimaryContactId: string | null }>(
        `/admin/customer-contacts/${contactId}/offboard`,
        { method: 'POST', body: JSON.stringify(body) }
      );
    },

    // ── Firmenlevel-Portalverwaltung ──────────────────────────────────────────

    async syncCustomers() {
      return request<{ provider: string; synced: number; errors: string[] }>(
        '/admin/customers/sync',
        { method: 'POST' }
      );
    },

    async listCustomers(params?: { q?: string; isActive?: boolean; cursor?: string; limit?: number }) {
      const qs = new URLSearchParams();
      if (params?.q) qs.set('q', params.q);
      if (params?.isActive !== undefined) qs.set('isActive', String(params.isActive));
      if (params?.cursor) qs.set('cursor', params.cursor);
      if (params?.limit) qs.set('limit', String(params.limit));
      const query = qs.toString();
      return request<{
        items: Array<{
          id: string;
          name: string;
          cwCompanyId: number | null;
          weclappCustomerId: string | null;
          customerNumber: string | null;
          fixbyteCustomerNumber: string | null;
          isActive: boolean;
          createdAt: string;
          contactCount: number;
          activeSessionCount: number;
          activeMagicLink: { id: string; createdAt: string; expiresAt: string | null; label: string | null; verifyUrl: string | null } | null;
        }>;
        nextCursor: string | null;
      }>(`/admin/customers${query ? `?${query}` : ''}`);
    },

    async getCustomer(customerId: string) {
      return request<{
        id: string;
        name: string;
        cwCompanyId: number | null;
        weclappCustomerId: string | null;
        customerNumber: string | null;
        fixbyteCustomerNumber: string | null;
        isActive: boolean;
        tags: string[];
        createdAt: string;
        shopDiscountPercent: number | null;
        /** Anzahl archivierter (widerrufen/abgelaufen/verwendet/manuell) Links — Backend zählt serverseitig. */
        archivedLinkCount: number;
        contacts: Array<{
          id: string;
          name: string;
          email: string;
          role: string;
          isActive: boolean;
          portalEnabled: boolean;
          isPortalDummy: boolean;
          createdAt: string;
          _count: { sessions: number };
        }>;
        magicLinkTokens: MagicLinkRow[];
      }>(`/admin/customers/${customerId}`);
    },

    async createCustomerPortalLink(
      customerId: string,
      body: {
        label?: string;
        ttlMinutes?: number | null;
        onboardingRoutineId?: string;
        role?: 'ADMIN' | 'MITARBEITER' | 'GAST';
        /** Schalter je Bereich wie in „Zugriff im Portal" */
        areas?: Record<string, boolean>;
        quoteAccept?: boolean;
      }
    ) {
      return request<{ tokenId: string; rawToken: string; verifyUrl: string; expiresAt: string | null; onboardingCreatedCount: number }>(
        `/admin/customers/${customerId}/magic-link`,
        { method: 'POST', body: JSON.stringify(body) }
      );
    },

    /**
     * Portal-Zugangslinks einer Firma (cursor-paginiert).
     * view: 'archived' (Default backend) = widerrufen/abgelaufen/verwendet/manuell archiviert,
     * 'active' = nur gültige, 'all' = alle.
     * Backend: GET /admin/customers/:customerId/magic-links
     */
    async listCustomerPortalLinks(
      customerId: string,
      params?: { view?: 'active' | 'all' | 'archived'; cursor?: string; limit?: number }
    ) {
      const qs = new URLSearchParams();
      if (params?.view) qs.set('view', params.view);
      if (params?.cursor) qs.set('cursor', params.cursor);
      if (params?.limit !== undefined) qs.set('limit', String(params.limit));
      const query = qs.toString();
      return request<{ items: MagicLinkRow[]; nextCursor: string | null }>(
        `/admin/customers/${customerId}/magic-links${query ? `?${query}` : ''}`
      );
    },

    /**
     * Aktive Portal-Sessions (eingeloggte Geräte) der gesamten Firma — über alle Kontakte.
     * Backend: GET /admin/customers/:customerId/sessions
     */
    async listCustomerCompanySessions(customerId: string) {
      return request<{
        items: Array<{
          id: string;
          contactId: string;
          contactName: string | null;
          contactRole: string | null;
          isPortalDummy: boolean;
          deviceLabel: string | null;
          createdAt: string;
          lastSeenAt: string;
          expiresAt: string;
        }>;
      }>(`/admin/customers/${customerId}/sessions`);
    },

    /**
     * Beendet alle aktiven Portal-Sessions der Firma.
     * Backend: POST /admin/customers/:customerId/sessions/revoke-all
     */
    async revokeAllCustomerCompanySessions(customerId: string) {
      return request<{ ok: boolean; revokedCount: number }>(
        `/admin/customers/${customerId}/sessions/revoke-all`,
        { method: 'POST' }
      );
    },

    async previewCustomerWelcomeEmail(
      customerId: string,
      body: { introNote?: string; displayName?: string }
    ) {
      return request<{ subject: string; html: string }>(
        `/admin/customers/${customerId}/welcome-email/preview`,
        { method: 'POST', body: JSON.stringify(body) }
      );
    },

    async sendCustomerWelcomeEmail(
      customerId: string,
      body: { introNote?: string; contactIds: string[] }
    ) {
      return request<{ sent: number; failed: number; total: number }>(
        `/admin/customers/${customerId}/welcome-email`,
        { method: 'POST', body: JSON.stringify(body) }
      );
    },

    async getContactGroups(customerId: string) {
      return request<{
        customerId: string;
        customerName: string;
        ticketVisibility: 'OPEN' | 'GROUPED';
        groups: Array<{
          id: string;
          name: string;
          cwSiteId: number | null;
          weclappCategoryId: string | null;
          contactCount: number;
          createdAt: string;
        }>;
      }>(`/admin/customers/${customerId}/contact-groups`);
    },

    async createContactGroup(customerId: string, body: { name: string; cwSiteId?: number | null; weclappCategoryId?: string | null }) {
      return request<{ group: { id: string; name: string; cwSiteId: number | null; weclappCategoryId: string | null } }>(
        `/admin/customers/${customerId}/contact-groups`,
        { method: 'POST', body: JSON.stringify(body) }
      );
    },

    async updateContactGroup(customerId: string, groupId: string, body: { name?: string; cwSiteId?: number | null; weclappCategoryId?: string | null }) {
      return request<{ group: { id: string; name: string; cwSiteId: number | null; weclappCategoryId: string | null } }>(
        `/admin/customers/${customerId}/contact-groups/${groupId}`,
        { method: 'PATCH', body: JSON.stringify(body) }
      );
    },

    async deleteContactGroup(customerId: string, groupId: string) {
      return request<{ success: boolean }>(
        `/admin/customers/${customerId}/contact-groups/${groupId}`,
        { method: 'DELETE' }
      );
    },

    // ── Globale (kundenübergreifende) Abteilungen ──────────────────────────
    async getGlobalContactGroups() {
      return request<{
        groups: Array<{
          id: string;
          name: string;
          cwSiteId: number | null;
          weclappCategoryId: string | null;
          contactCount: number;
          createdAt: string;
        }>;
      }>(`/admin/global-contact-groups`);
    },

    async createGlobalContactGroup(body: { name: string }) {
      return request<{ group: { id: string; name: string } }>(
        `/admin/global-contact-groups`,
        { method: 'POST', body: JSON.stringify(body) }
      );
    },

    async updateGlobalContactGroup(
      id: string,
      body: { name?: string }
    ) {
      return request<{ group: { id: string; name: string } }>(
        `/admin/global-contact-groups/${id}`,
        { method: 'PATCH', body: JSON.stringify(body) }
      );
    },

    async deleteGlobalContactGroup(id: string) {
      return request<{ ok: boolean }>(`/admin/global-contact-groups/${id}`, { method: 'DELETE' });
    },

    async setTicketVisibility(customerId: string, ticketVisibility: 'OPEN' | 'GROUPED') {
      return request<{ success: boolean; ticketVisibility: 'OPEN' | 'GROUPED' }>(
        `/admin/customers/${customerId}/ticket-visibility`,
        { method: 'PATCH', body: JSON.stringify({ ticketVisibility }) }
      );
    },

    async assignContactGroup(contactId: string, contactGroupId: string | null) {
      return request<{ success: boolean }>(
        `/admin/customer-contacts/${contactId}/contact-group`,
        { method: 'PATCH', body: JSON.stringify({ contactGroupId }) }
      );
    },

    // Analytics endpoints (Phase 3)

    async getAnalyticsOverview() {
      return request<{
        logins: { today: number; week: number; month: number };
        sessions: { active: number; activeContacts: number };
        feedback: { count: number; averageCsat: number | null };
        contacts: { total: number };
      }>('/admin/customer-portal/analytics/overview');
    },

    async getLoginTrend(params?: { from?: string; to?: string; granularity?: string }) {
      const query = new URLSearchParams();
      if (params?.from) query.append('from', params.from);
      if (params?.to) query.append('to', params.to);
      if (params?.granularity) query.append('granularity', params.granularity);
      const qs = query.toString();
      return request<{
        data: Array<{ date: string; logins: number }>;
      }>(`/admin/customer-portal/analytics/login-trend${qs ? '?' + qs : ''}`);
    },

    async getSessionAnalytics(params?: { from?: string; to?: string }) {
      const query = new URLSearchParams();
      if (params?.from) query.append('from', params.from);
      if (params?.to) query.append('to', params.to);
      const qs = query.toString();
      return request<{
        avgDurationSeconds: number;
        totalSessions: number;
        topDevices: Array<{ userAgent: string | null; sessionCount: number }>;
      }>(`/admin/customer-portal/analytics/sessions${qs ? '?' + qs : ''}`);
    },

    async getTopPages(params?: { from?: string; to?: string }) {
      const query = new URLSearchParams();
      if (params?.from) query.append('from', params.from);
      if (params?.to) query.append('to', params.to);
      const qs = query.toString();
      return request<{
        data: Array<{ path: string | null; views: number }>;
      }>(`/admin/customer-portal/analytics/top-pages${qs ? '?' + qs : ''}`);
    },

    async getTopClicks(params?: { from?: string; to?: string }) {
      const query = new URLSearchParams();
      if (params?.from) query.append('from', params.from);
      if (params?.to) query.append('to', params.to);
      const qs = query.toString();
      return request<{
        data: Array<{ target: string | null; clicks: number }>;
      }>(`/admin/customer-portal/analytics/top-clicks${qs ? '?' + qs : ''}`);
    },

    async searchAuditLog(params: {
      q?: string;
      contactId?: string;
      category?: string;
      action?: string;
      result?: string;
      cursor?: string;
      limit?: number;
    }) {
      const query = new URLSearchParams();
      if (params.q) query.append('q', params.q);
      if (params.contactId) query.append('contactId', params.contactId);
      if (params.category) query.append('category', params.category);
      if (params.action) query.append('action', params.action);
      if (params.result) query.append('result', params.result);
      if (params.cursor) query.append('cursor', params.cursor);
      if (params.limit) query.append('limit', String(params.limit));
      const qs = query.toString();
      return request<{
        data: Array<{
          id: string;
          contactId: string;
          contactName: string | null;
          contactEmail: string | null;
          category: string;
          action: string;
          result: string;
          metadata: Record<string, unknown>;
          createdAt: string;
        }>;
        pagination: { hasMore: boolean; nextCursor: string | null };
      }>(`/admin/customer-portal/analytics/audit-log${qs ? '?' + qs : ''}`);
    },

    // ─── Admin: Customer-Onboarding-Verwaltung (Phase 1) ──────────────────────
    async listCustomerOnboarding(params: {
      q?: string;
      variant?: 'NEW_CUSTOMER' | 'EXISTING_CUSTOMER';
      progressMin?: number;
      progressMax?: number;
      startedDaysAgo?: number;
      cursor?: string;
      limit?: number;
    } = {}) {
      const qs = new URLSearchParams();
      if (params.q) qs.set('q', params.q);
      if (params.variant) qs.set('variant', params.variant);
      if (params.progressMin !== undefined) qs.set('progressMin', String(params.progressMin));
      if (params.progressMax !== undefined) qs.set('progressMax', String(params.progressMax));
      if (params.startedDaysAgo !== undefined) qs.set('startedDaysAgo', String(params.startedDaysAgo));
      if (params.cursor) qs.set('cursor', params.cursor);
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      const query = qs.toString();
      return request<{
        items: Array<{
          id: string;
          contactId: string;
          contact: { name: string; email: string };
          variant: string;
          startedAt: string;
          tourCompletedAt: string | null;
          checklistState: Record<string, boolean>;
          progressPercent: number;
          day14Sent: boolean;
          day30Sent: boolean;
          day90Sent: boolean;
          daysSinceStart: number;
        }>;
        nextCursor: string | null;
      }>(`/admin/customer-onboarding${query ? `?${query}` : ''}`);
    },

    async getCustomerOnboarding(contactId: string) {
      return request<{
        onboarding: {
          id: string;
          contactId: string;
          variant: string;
          startedAt: string;
          tourCompletedAt: string | null;
          checklistState: Record<string, boolean>;
          progressPercent: number;
          day14Sent: boolean;
          day30Sent: boolean;
          day90Sent: boolean;
          daysSinceStart: number;
        };
        contact: {
          id: string;
          name: string;
          email: string;
          cwCompanyId: number | null;
        };
        checklistItems: Array<{
          id: string;
          label: string;
          completed: boolean;
          completedAt: string | null;
        }>;
        milestones?: Record<string, { sentAt?: string | null }>;
      }>(`/admin/customer-onboarding/${contactId}`);
    },

    async updateCustomerOnboarding(contactId: string, data: {
      checklistItem?: { id: string; completed: boolean };
    }) {
      return request<{
        id: string;
        checklistState: Record<string, boolean>;
        progressPercent: number;
      }>(`/admin/customer-onboarding/${contactId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },

    async getEmailPreviews() {
      return request<{ templates: EmailPreviewTemplate[] }>('/admin/email-previews');
    },

    async getPdfPreview(
      type:
        | 'freigabe-beleg'
        | 'service-bericht'
        | 'liefernachweis'
        | 'inventur'
        | 'rueckgabe'
        | 'kunden-report'
        | 'infrastruktur-export'
        | 'geraete-karte',
    ): Promise<Blob> {
      const res = await fetch(`${API_BASE}/admin/pdf-preview/${type}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        let msg = `PDF-Vorschau fehlgeschlagen (HTTP ${res.status})`;
        try {
          const j = (await res.json()) as { error?: string };
          if (j?.error) msg = j.error;
        } catch { /* ignore */ }
        throw new Error(msg);
      }
      return res.blob();
    },

    // ─── Admin: Onboarding-Routinen ───────────────────────────────────────────
    async listOnboardingRoutines() {
      return request<{
        items: Array<{
          id: string;
          name: string;
          description: string | null;
          checklistItems: Array<{ id: string; label: string }>;
          isDefault: boolean;
          createdAt: string;
          /** Anzahl laufender Onboardings, die diese Routine nutzen (blockiert das Löschen). */
          activeOnboardingCount: number;
        }>;
      }>('/admin/onboarding-routines');
    },

    async createOnboardingRoutine(data: {
      name: string;
      description?: string;
      checklistItems: Array<{ id: string; label: string }>;
      isDefault?: boolean;
    }) {
      return request<{
        id: string;
        name: string;
        description: string | null;
        checklistItems: Array<{ id: string; label: string }>;
        isDefault: boolean;
        createdAt: string;
      }>('/admin/onboarding-routines', { method: 'POST', body: JSON.stringify(data) });
    },

    async updateOnboardingRoutine(id: string, data: {
      name?: string;
      description?: string;
      checklistItems?: Array<{ id: string; label: string }>;
      isDefault?: boolean;
    }) {
      return request<{
        id: string;
        name: string;
        description: string | null;
        checklistItems: Array<{ id: string; label: string }>;
        isDefault: boolean;
      }>(`/admin/onboarding-routines/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    },

    async deleteOnboardingRoutine(id: string) {
      return request<{ ok: boolean }>(`/admin/onboarding-routines/${id}`, { method: 'DELETE' });
    },

    async setDefaultOnboardingRoutine(id: string) {
      return request<{ ok: boolean }>(`/admin/onboarding-routines/${id}/default`, { method: 'PATCH' });
    },

    async updateContactPhone(contactId: string, phone: string | null) {
      return request<{ ok: boolean; phone: string | null }>(
        `/admin/customer-contacts/${contactId}/phone`,
        { method: 'PATCH', body: JSON.stringify({ phone }) }
      );
    },

    // ── Weclapp Mirror ────────────────────────────────────────────────────────

    async getWeclappMirrorStatus() {
      return request<{
        tickets: number;
        deletedTickets: number;
        /** Im Papierkorb liegende Tickets — zählen nicht zum sichtbaren Bestand. */
        trashedTickets: number;
        comments: number;
        timeEntries: number;
        documents: number;
        mails: number;
        history: number;
        /** Tickets, deren Suchtext noch ohne Kommentare/Mails ist — der Backfill-Job holt sie nach. */
        searchBacklog?: number;
        /** Jüngstes `syncedAt` im Spiegel (Ticketkopf). */
        lastSyncedAt: string | null;
        /** Jüngstes `detailSyncedAt` — Kommentare/Zeiten/Anhänge eines Tickets. */
        lastDetailSyncedAt: string | null;
        readsEnabled: boolean;
        /** Cursor-Stand als weclapp `lastModifiedDate` in ms. */
        cursorLastModifiedDate: number | null;
        cursorAt: string | null;
        backfillComplete: boolean;
        backfillCompletedAt: string | null;
        syncErrors: { openErrors: number; resolvedLast24h: number };
        /** Lauf-Protokoll in Kurzform — null, solange noch kein Lauf erfasst wurde. */
        runs: {
          lastRun: WeclappMirrorRun | null;
          lastSuccessfulRun: WeclappMirrorRun | null;
          lastRunWithChanges: WeclappMirrorRun | null;
          last24h: { runs: number; processed: number; errors: number };
        } | null;
        /**
         * Anhang-DATEIEN im Spiegel — nicht zu verwechseln mit `documents` (Metadaten).
         * `null`, solange die Inhalts-Migration nicht angewendet ist.
         */
        documentContent: {
          documents: number;
          withContent: number;
          withoutContent: number;
          neverTried: number;
          tooLarge: number;
          notFound: number;
          failed: number;
          storedBytes: number;
        } | null;
        /** Gesicherte Auswahllisten (Status, Prioritäten, …) für den Sperrfall. */
        catalogs: Array<{ catalog: string; label: string; rows: number; savedAt: string | null }>;
      }>('/admin/weclapp-mirror/status');
    },

    /** Lauf-Protokoll des Spiegels (Admin → Weclapp Mirror → Sync-Verlauf). */
    async getWeclappMirrorRuns(params?: {
      area?: 'tickets' | 'comments' | 'timeRecords';
      only?: 'errors' | 'changes';
      limit?: number;
      offset?: number;
    }) {
      const q = new URLSearchParams();
      if (params?.area) q.set('area', params.area);
      if (params?.only) q.set('only', params.only);
      if (params?.limit !== undefined) q.set('limit', String(params.limit));
      if (params?.offset !== undefined) q.set('offset', String(params.offset));
      return request<{
        runs: WeclappMirrorRun[];
        total: number;
        limit: number;
        offset: number;
      }>(`/admin/weclapp-mirror/runs?${q}`);
    },

    async getWeclappMirrorErrors(params?: { resolved?: boolean; limit?: number; offset?: number }) {
      const q = new URLSearchParams();
      if (params?.resolved !== undefined) q.set('resolved', String(params.resolved));
      if (params?.limit !== undefined) q.set('limit', String(params.limit));
      if (params?.offset !== undefined) q.set('offset', String(params.offset));
      return request<{
        errors: Array<{
          id: number;
          ticketId: string;
          subEntity: string;
          errorMessage: string;
          firstOccurredAt: string;
          lastOccurredAt: string;
          occurrenceCount: number;
          resolvedAt: string | null;
        }>;
        total: number;
        limit: number;
        offset: number;
      }>(`/admin/weclapp-mirror/errors?${q}`);
    },

    async setWeclappMirrorReadsEnabled(enabled: boolean) {
      return request<{ success: boolean; enabled: boolean }>(
        '/admin/weclapp-mirror/flag',
        { method: 'POST', body: JSON.stringify({ enabled }) }
      );
    },

    async triggerWeclappMirrorBackfill(params?: { limit?: number; full?: boolean }) {
      return request<{ accepted: boolean; limit: number; full: boolean }>(
        '/admin/weclapp-mirror/backfill',
        { method: 'POST', body: JSON.stringify({ limit: params?.limit ?? 200, full: params?.full ?? false }) }
      );
    },

    /** Fehlende Ticket-Anhänge im Spiegel nachtragen (Hintergrundlauf). */
    async triggerWeclappDocumentBackfill() {
      return request<{ accepted: boolean }>('/admin/weclapp-mirror/document-backfill', {
        method: 'POST',
      });
    },

    /** Laufenden Nachtrag beenden (nach dem aktuellen Ticket). */
    async stopWeclappDocumentBackfill() {
      return request<{ stopping: boolean }>('/admin/weclapp-mirror/document-backfill/stop', {
        method: 'POST',
      });
    },

    async getWeclappDocumentBackfillStatus() {
      return request<{
        running: boolean;
        startedAt: string | null;
        finishedAt: string | null;
        strategy: 'bulk' | 'per-ticket' | null;
        pagesFetched: number;
        documentsSeen: number;
        documentsRestored: number;
        ticketsTouched: number;
        ticketsScanned: number;
        ticketsTotal: number;
        stopping: boolean;
        /** Beleg zum Gegenprüfen: welche Datei an welchem Ticket zurückkam. */
        restoredSamples: { ticketId: string; ticketNumber: string | null; fileName: string }[];
        errors: string[];
        /** Der Metadaten-Nachtrag stößt den Datei-Nachtrag selbst an, zeigt ihn aber nicht. */
        contentBackfillStarted: boolean;
      }>('/admin/weclapp-mirror/document-backfill/status');
    },

    /**
     * Fehlende Anhang-DATEIEN nachladen (Hintergrundlauf, Stunden).
     *
     * Eigener Lauf neben dem Metadaten-Nachtrag: Die Metadaten sind in Minuten durch, die
     * Dateien brauchen bei rund 17.000 Tickets deutlich länger — in einem Balken vermischt
     * wäre der Fortschritt nicht zu lesen.
     */
    async triggerWeclappDocumentContentBackfill() {
      return request<{ accepted: boolean }>('/admin/weclapp-mirror/document-content-backfill', {
        method: 'POST',
      });
    },

    async stopWeclappDocumentContentBackfill() {
      return request<{ stopping: boolean }>(
        '/admin/weclapp-mirror/document-content-backfill/stop',
        { method: 'POST' }
      );
    },

    async getWeclappDocumentContentBackfillStatus() {
      return request<{
        running: boolean;
        startedAt: string | null;
        finishedAt: string | null;
        documentsTotal: number;
        documentsProcessed: number;
        documentsStored: number;
        documentsSkipped: number;
        documentsTooLarge: number;
        documentsMissing: number;
        documentsFailed: number;
        bytesStored: number;
        stopping: boolean;
        errors: string[];
      }>('/admin/weclapp-mirror/document-content-backfill/status');
    },

    async retryWeclappMirrorErrors() {
      return request<{ accepted: boolean; openErrors: number }>(
        '/admin/weclapp-mirror/retry-errors',
        { method: 'POST' }
      );
    },

  };
}

export interface KianUsagePerson {
  userId: string | null;
  name: string;
  role: string | null;
  calls: number;
  failed: number;
  costEur: number;
  costMonthEur: number;
  budgetEur: number | null;
  byGroup: Record<string, { calls: number; costEur: number }>;
  activeDays: number;
}
export interface KianUsageReport {
  from: string;
  to: string;
  monthStart: string;
  budgetEur: number;
  totals: { calls: number; failed: number; costEur: number };
  people: KianUsagePerson[];
  daily: Array<{ day: string; byPerson: Record<string, number> }>;
  groups: string[];
}
